import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building, MapPin, Mail, Users } from 'lucide-react';

const Impressum = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to German version if not already on it, but only once
  React.useEffect(() => {
    // Check if we're already on the German impressum path
    if (location.pathname === '/de/impressum') {
      return; // Already on the correct path, no need to redirect
    }
    
    // Only redirect if we're not on German and not already redirecting
    if (currentLanguage !== 'de' && !location.pathname.includes('/de/impressum')) {
      navigate('/de/impressum', { replace: true });
    }
  }, [currentLanguage, navigate, location.pathname]);

  // Don't render anything while redirecting
  if (currentLanguage !== 'de' && location.pathname !== '/de/impressum') {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container-custom py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Building className="h-12 w-12 text-primary-600" />
            </div>
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">
              Impressum
            </h1>
            <p className="text-lg text-neutral-600">
              Angaben gemäß § 5 TMG
            </p>
          </div>

          {/* Company Information */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-6 flex items-center">
              <Building className="h-6 w-6 text-primary-600 mr-3" />
              Angaben zum Diensteanbieter
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">Firmenname</h3>
                  <p className="text-neutral-700">Klimaneutral Berlin 2030 GmbH</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">Rechtsform</h3>
                  <p className="text-neutral-700">Gesellschaft mit beschränkter Haftung</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">Registergericht</h3>
                  <p className="text-neutral-700">Amtsgericht Berlin (Charlottenburg)</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">Registernummer</h3>
                  <p className="text-neutral-700">HRB 109460 B</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">Steuernummer</h3>
                  <p className="text-neutral-700">2955332327</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2 flex items-center">
                    <MapPin className="h-5 w-5 text-primary-600 mr-2" />
                    Anschrift
                  </h3>
                  <div className="text-neutral-700">
                    <p>Stresemannstr. 23</p>
                    <p>D-10963 Berlin</p>
                    <p>Deutschland</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2 flex items-center">
                    <Mail className="h-5 w-5 text-primary-600 mr-2" />
                    E-Mail
                  </h3>
                  <p className="text-neutral-700">info@co2neutralpage.com</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Vertretungsberechtigte Person */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
                <Users className="h-6 w-6 text-primary-600 mr-3" />
                Vertretungsberechtigte Person
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Die Gesellschaft wird vertreten durch den Geschäftsführer.
                </p>
              </div>
            </div>

            {/* Umsatzsteuer-ID */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Umsatzsteuer-ID
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:
                </p>
                <p className="font-semibold">DE257906975</p>
              </div>
            </div>

            {/* Berufsaufsichtsbehörde */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Berufsaufsichtsbehörde
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Zuständige Aufsichtsbehörde für die Berufsausübung:
                </p>
                <p>Senatsverwaltung für Wirtschaft, Energie und Betriebe</p>
                <p>Martin-Luther-Straße 105</p>
                <p>10825 Berlin</p>
                <p>Deutschland</p>
              </div>
            </div>

            {/* Berufsbezeichnung und berufsrechtliche Regelungen */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Berufsbezeichnung und berufsrechtliche Regelungen
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  <strong>Berufsbezeichnung:</strong> CO₂-Neutralitäts-Zertifizierung
                </p>
                <p>
                  <strong>Zuständige Kammer:</strong> IHK Berlin
                </p>
                <p>
                  <strong>Verliehen durch:</strong> Deutschland
                </p>
              </div>
            </div>

            {/* Redaktionell verantwortlich */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Redaktionell verantwortlich
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Für den Inhalt verantwortlich gemäß § 55 Abs. 2 RStV:
                </p>
                <p>KlimaNeutral Berlin 2030 GmbH</p>
                <p>Stresemannstr. 23</p>
                <p>D-10963 Berlin</p>
                <p>Deutschland</p>
              </div>
            </div>

            {/* EU-Streitschlichtung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                EU-Streitschlichtung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Die Europäische Kommission stellt eine Plattform zur 
                  Online-Streitbeilegung (OS) bereit: 
                  <a href="https://ec.europa.eu/consumers/odr/" 
                     className="text-primary-600 hover:text-primary-700 underline ml-1">
                    https://ec.europa.eu/consumers/odr/
                  </a>
                </p>
                <p className="mt-4">
                  Unsere E-Mail-Adresse finden Sie oben im Impressum.
                </p>
              </div>
            </div>

            {/* Verbraucherstreitbeilegung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Verbraucherstreitbeilegung/Universalschlichtungsstelle
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Wir sind nicht bereit oder verpflichtet, an 
                  Streitbeilegungsverfahren vor einer 
                  Verbraucherschlichtungsstelle teilzunehmen.
                </p>
              </div>
            </div>

            {/* Haftung für Inhalte */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Haftung für Inhalte
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene 
                  Inhalte auf diesen Seiten nach den allgemeinen Gesetzen 
                  verantwortlich. Nach §§ 8 bis 10 TMG sind wir als 
                  Diensteanbieter jedoch nicht verpflichtet, übermittelte 
                  oder gespeicherte fremde Informationen zu überwachen oder 
                  nach Umständen zu forschen, die auf eine rechtswidrige 
                  Tätigkeit hinweisen.
                </p>
                <p className="mt-4">
                  Verpflichtungen zur Entfernung oder Sperrung der Nutzung 
                  von Informationen nach den allgemeinen Gesetzen bleiben 
                  hiervon unberührt. Eine diesbezügliche Haftung ist jedoch 
                  erst ab dem Zeitpunkt der Kenntnis einer konkreten 
                  Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden 
                  Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
                </p>
              </div>
            </div>

            {/* Haftung für Links */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Haftung für Links
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Unser Angebot enthält Links zu externen Websites Dritter, 
                  auf deren Inhalte wir keinen Einfluss haben. Deshalb können 
                  wir für diese fremden Inhalte auch keine Gewähr übernehmen. 
                  Für die Inhalte der verlinkten Seiten ist stets der jeweilige 
                  Anbieter oder Betreiber der Seiten verantwortlich. Die 
                  verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf 
                  mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte 
                  waren zum Zeitpunkt der Verlinkung nicht erkennbar.
                </p>
                <p className="mt-4">
                  Eine permanente inhaltliche Kontrolle der verlinkten Seiten 
                  ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung 
                  nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen 
                  werden wir derartige Links umgehend entfernen.
                </p>
              </div>
            </div>

            {/* Urheberrecht */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Urheberrecht
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke 
                  auf diesen Seiten unterliegen dem deutschen Urheberrecht. 
                  Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art 
                  der Verwertung außerhalb der Grenzen des Urheberrechtes 
                  bedürfen der schriftlichen Zustimmung des jeweiligen Autors 
                  bzw. Erstellers. Downloads und Kopien dieser Seite sind nur 
                  für den privaten, nicht kommerziellen Gebrauch gestattet.
                </p>
                <p className="mt-4">
                  Soweit die Inhalte auf dieser Seite nicht vom Betreiber 
                  erstellt wurden, werden die Urheberrechte Dritter beachtet. 
                  Insbesondere werden Inhalte Dritter als solche gekennzeichnet. 
                  Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam 
                  werden, bitten wir um einen entsprechenden Hinweis. Bei 
                  Bekanntwerden von Rechtsverletzungen werden wir derartige 
                  Inhalte umgehend entfernen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Impressum; 